import posthog from 'posthog-js'
import React, {useState, useEffect} from 'react'
import {useXpertKnowledgePublicTutorialContext, XpertKnowledgePublicTutorialContextProvider} from './XpertKnowledgePublicTutorialContext'
import {Block, Col, Row} from 'jsxstyle'
import StarRating from '../../components/StarRating/StarRating'
import XpertKnowledgeTutorialSection from './../XpertKnowledgeTutorial/components/XpertKnowledgeTutorialSection'
import XpertKnowledgeSidebar from './../XpertKnowledgeTutorial/components/XpertKnowledgeTutorialSidebar'
import {Outlet} from 'react-router-dom'
import Loader from '../../components/Loader'
import i18n from '../../common/i18n'
import {useResponsiveBreakpoints} from '../../hooks/mediaQueries'
import SidebarMenuButton from './components/SidebarMenuButton'

const XpertKnowledgePublicTutorial: React.FC = () => {
  const {xpertKnowledgeId, publicXpertKnowledgeTutorial, xpertKnowledgeSections, submitPublicRating} = useXpertKnowledgePublicTutorialContext()
  const {tablet, laptop} = useResponsiveBreakpoints()

  const firstSectionId =
    xpertKnowledgeSections.length > 0
      ? xpertKnowledgeSections.reduce((minSection, currentSection) => (currentSection.position < minSection.position ? currentSection : minSection))
          .id
      : undefined

  const [selectedSectionId, setSelectedSectionId] = useState<number | undefined>(firstSectionId)
  const [selectedSubSectionId, setSelectedSubSectionId] = useState<number | undefined>()
  const [sidebarVisible, setSidebarVisible] = useState<boolean>(false)
  const [isScrolled, setIsScrolled] = useState<boolean>(false)

  useEffect(() => {
    const hash = window.location.hash
    if (hash) {
      const element = document.querySelector(hash)
      if (element) element.scrollIntoView({behavior: 'smooth', block: 'start'})
    }
  }, [selectedSectionId, selectedSubSectionId])

  useEffect(() => {
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement
      const scrollTop = target.scrollTop
      setIsScrolled(scrollTop > 100)
    }

    const mainContent = document.getElementById('main-content')
    if (mainContent) {
      mainContent.addEventListener('scroll', handleScroll)
      return () => mainContent.removeEventListener('scroll', handleScroll)
    }
  }, [])

  if (!publicXpertKnowledgeTutorial || !xpertKnowledgeSections) {
    return (
      <Col height="100%" width="100%" justifyContent="center" alignItems="center" gap="20px">
        <Block>
          <Loader size={75} />
        </Block>
        <Block fontWeight={500} lineHeight="60px" fontSize="34px">
          {i18n.t('xk.tutorial.loading')}
        </Block>
      </Col>
    )
  }

  return (
    <>
      <Block
        position="relative"
        height="100%"
        width="100%"
        display="flex"
        flexDirection="row"
        transition="all 0.3s ease"
        {...(!laptop && {
          flexDirection: 'column',
        })}
      >
        <Block
          position="relative"
          height="100%"
          width="329px"
          minWidth="240px"
          transform="translateX(0)"
          transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
          {...(!laptop && {
            position: 'fixed',
            top: '0',
            left: '0',
            zIndex: 999,
            height: '100vh',
            width: '330px',
            minWidth: '330px',
            transform: sidebarVisible ? 'translateX(0)' : 'translateX(-100%)',
            boxShadow: sidebarVisible ? '4px 0 20px rgba(0, 0, 0, 0.15)' : 'none',
          })}
        >
          <XpertKnowledgeSidebar
            selectedSectionId={selectedSectionId ?? firstSectionId}
            setSelectedSectionId={setSelectedSectionId}
            selectedSubSectionId={selectedSubSectionId}
            setSelectedSubSectionId={setSelectedSubSectionId}
            selectedXpertKnowledgeTutorial={publicXpertKnowledgeTutorial}
            onSectionSelect={() => !laptop && setSidebarVisible(false)}
          />
        </Block>

        {/* Mobile Overlay */}
        {!laptop && (
          <Block
            position="fixed"
            top="0"
            left="0"
            width="100%"
            height="100%"
            backgroundColor="rgba(0, 0, 0, 0.6)"
            zIndex={998}
            opacity={sidebarVisible ? 1 : 0}
            pointerEvents={sidebarVisible ? 'auto' : 'none'}
            transition="all 0.3s ease"
            backdropFilter="blur(4px)"
            props={{
              onClick: () => setSidebarVisible(false),
            }}
          />
        )}

        {/* Sticky Header for Mobile/Tablet */}
        {!laptop && (
          <Row
            position="sticky"
            top="0"
            left="0"
            right="0"
            gap="8px"
            backgroundColor="white"
            borderBottom="1px solid #DADCE0"
            padding="16px"
            zIndex={100}
            justifyContent="space-between"
            alignItems="center"
            boxShadow={isScrolled ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none'}
            transition="all 0.3s ease"
            {...(tablet && {
              padding: '24px',
            })}
          >
            <Block fontSize="18px" fontWeight={600} color="black" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              {publicXpertKnowledgeTutorial.title}
            </Block>
            <SidebarMenuButton setSidebarVisible={() => setSidebarVisible(!sidebarVisible)} sidebarVisible />
          </Row>
        )}

        <Col
          gap="24px"
          width="100%"
          height="100vh"
          overflowY="scroll"
          boxSizing="border-box"
          padding="26px 70px 34px 100px"
          transition="all 0.3s ease"
          {...(!laptop && {
            padding: '20px 16px 34px 16px',
          })}
        >
          {laptop && (
            <Block lineHeight="48px" color="black" marginTop="10px" marginBottom="12px" fontSize="32px" fontWeight={500} transition="all 0.3s ease">
              {publicXpertKnowledgeTutorial.title}
            </Block>
          )}

          <Block
            fontWeight={400}
            fontSize="14px"
            lineHeight="22px"
            marginBottom="8px"
            transition="all 0.3s ease"
            {...(!tablet && {
              fontSize: '13px',
              lineHeight: '20px',
            })}
          >
            {publicXpertKnowledgeTutorial.description}
          </Block>

          {publicXpertKnowledgeTutorial.audioBlob && (
            <Block
              marginBottom="8px"
              width="40%"
              transition="all 0.3s ease"
              {...(!tablet && {
                width: '100%',
              })}
              {...(tablet &&
                !laptop && {
                  width: '60%',
                })}
            >
              <audio
                controls
                style={{width: '100%'}}
                onPlay={() => {
                  posthog.capture('audio_played')
                }}
              >
                <source src={publicXpertKnowledgeTutorial.audioBlob.url} type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
            </Block>
          )}

          <Block
            justifyContent="space-between"
            alignItems="center"
            display="flex"
            flexDirection="row"
            transition="all 0.3s ease"
            {...(!tablet && {
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '12px',
            })}
          >
            <Block
              alignItems="center"
              display="flex"
              flexDirection="row"
              gap="10px"
              transition="all 0.3s ease"
              {...(!tablet && {
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '8px',
              })}
            >
              <Block
                fontSize="14px"
                lineHeight="24px"
                fontWeight={400}
                color="#909090"
                transition="all 0.3s ease"
                {...(!tablet && {
                  fontSize: '13px',
                  marginBottom: '4px',
                })}
              >
                {i18n.t('xk.tutorial.rate')}
              </Block>
              <StarRating
                rating={publicXpertKnowledgeTutorial.userRating?.rating ?? 0}
                editable={true}
                onChange={(newRating) => {
                  if (xpertKnowledgeId) {
                    submitPublicRating(xpertKnowledgeId, newRating)
                    posthog.capture('rating_submited', {
                      rating_value: newRating,
                    })
                  }
                }}
              />
            </Block>
          </Block>

          <Block borderBottom="1px solid #DADCE0" marginBottom="24px" />

          {xpertKnowledgeSections.map((section) => (
            <XpertKnowledgeTutorialSection key={section.id} xpertKnowledgeSection={section} />
          ))}
        </Col>
      </Block>
      <Outlet />
    </>
  )
}

export default () => (
  <XpertKnowledgePublicTutorialContextProvider>
    <XpertKnowledgePublicTutorial />
  </XpertKnowledgePublicTutorialContextProvider>
)
